import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import ViewResult from '../../component/ViewResult';
import useInterviewResults from '../../interviewOverview/useInterviewResults';
import EmailConfirmModal from '../../interviewOverview/EmailConfirmModal';
import '../../admin.css';

export default function FeedbackPage({ onLogout }) {
    const { t } = useTranslation();
    // eslint-disable-next-line no-unused-vars
    const { company_id, candidate_id, interview_id } = useParams();
    const navigate = useNavigate();

    const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
    const [checkingAccess, setCheckingAccess] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const {
        feedback,
        loadingFeedback,
        feedbackError,
        sessionData,
        isSendingEmail,
        emailSent,
        showEmailConfirmModal,
        emailRecipient,
        ccRecipients,
        setCcRecipients,
        setEmailRecipient,
        fetchInterviewResults,
        sendFeedbackEmail,
        prepareEmailSend,
        closeEmailModal,
    } = useInterviewResults();

    // Check if user is company admin
    useEffect(() => {
        const checkAccess = async () => {
            try {
                setCheckingAccess(true);

                // Get user from localStorage
                const userStr = localStorage.getItem('user') || localStorage.getItem('adminUser');
                if (!userStr) {
                    setAccessDenied(true);
                    setCheckingAccess(false);
                    return;
                }

                const user = JSON.parse(userStr);

                // Check if user is company admin
                const isCompanyAdminUser = Array.isArray(user?.admin_companies) && user.admin_companies.length > 0;

                if (!isCompanyAdminUser) {
                    setAccessDenied(true);
                    setCheckingAccess(false);
                    return;
                }

                // Optionally verify company_id matches user's admin_companies
                if (company_id && user.admin_companies && user.admin_companies.length > 0) {
                    // If company_id is provided, check if user has access to that company
                    // This assumes company_id might be a name or ID that matches admin_companies
                    // Adjust this logic based on your actual data structure
                    const hasAccess = user.admin_companies.some(company =>
                        company.toString() === company_id.toString() ||
                        company === company_id
                    );

                    if (!hasAccess && user.admin_companies.length > 1) {
                        // Only enforce if user has multiple companies
                        // If single company, allow access (company_id might be in different format)
                        setAccessDenied(true);
                        setCheckingAccess(false);
                        return;
                    }
                }

                setIsCompanyAdmin(true);

                // Fetch interview results
                if (interview_id) {
                    await fetchInterviewResults(interview_id);
                }
            } catch (err) {
                console.error('Error checking access:', err);
                setAccessDenied(true);
            } finally {
                setCheckingAccess(false);
            }
        };

        checkAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company_id, interview_id]);

    const handleClose = () => {
        // Navigate to candidates page
        navigate('/candidates');
    };

    const handleSendEmail = async (feedbackData, sessionId, candidateEmail, candidateName, htmlContent = null, filename = null, compactMode = false) => {
        try {
            // Prepare email send with HTML content and filename
            // This should open the EmailConfirmModal
            prepareEmailSend(candidateName, candidateEmail, htmlContent, filename, compactMode);
        } catch (error) {
            console.error('Error preparing email send:', error);
            setSnackbar({
                open: true,
                message: t('feedback.emailPrepareError', 'Failed to prepare email. Please try again.'),
                severity: 'error'
            });
        }
    };

    const handleConfirmEmail = async () => {
        if (!emailRecipient || !feedback) return;

        const emailData = {
            sessionId: feedback?.session,
            candidateEmail: emailRecipient.email,
            candidateName: emailRecipient.name,
            ccRecipients: ccRecipients.split(',').map(email => email.trim()).filter(email => email),
            html_content: emailRecipient.htmlContent,
            filename: emailRecipient.filename,
            compact_mode: emailRecipient.compactMode,
            feedback: feedback
        };

        const result = await sendFeedbackEmail(emailData);

        if (result.success) {
            setSnackbar({
                open: true,
                message: result.message || t('feedback.emailSentSuccess', 'Email sent successfully!'),
                severity: 'success'
            });
        } else {
            setSnackbar({
                open: true,
                message: result.error || t('feedback.emailSentError', 'Failed to send email'),
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Show loading state while checking access
    if (checkingAccess) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">{t('feedback.checkingAccess', 'Checking access...')}</p>
                </div>
            </div>
        );
    }

    // Show access denied
    if (accessDenied || !isCompanyAdmin) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        {t('feedback.accessDenied', 'Access Denied')}
                    </h2>
                    <p className="text-gray-600 mb-6">
                        {t('feedback.accessDeniedMessage', 'Only company administrators can view this page.')}
                    </p>
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        {t('feedback.goBack', 'Go Back')}
                    </button>
                </div>
            </div>
        );
    }

    // Show error if no interview_id
    if (!interview_id) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        {t('feedback.invalidRequest', 'Invalid Request')}
                    </h2>
                    <p className="text-gray-600 mb-6">
                        {t('feedback.invalidRequestMessage', 'Interview ID is required.')}
                    </p>
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        {t('feedback.goBack', 'Go Back')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Snackbar for notifications */}
            {snackbar.open && (
                <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${snackbar.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
                    } text-white`}>
                    <div className="flex items-center justify-between">
                        <span>{snackbar.message}</span>
                        <button
                            onClick={handleCloseSnackbar}
                            className="ml-4 text-white hover:text-gray-200"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* ViewResult Component - Display as page content, not modal */}
            <div className="w-full">
                <ViewResult
                    feedback={feedback}
                    loadingFeedback={loadingFeedback}
                    feedbackError={feedbackError}
                    onClose={handleClose}
                    sessionData={sessionData}
                    isSendingEmail={isSendingEmail}
                    emailSent={emailSent}
                    sendEmail={handleSendEmail}
                    callFrom="feedbackPage"
                />
            </div>

            {/* Email Confirmation Modal */}
            <EmailConfirmModal
                isOpen={showEmailConfirmModal}
                onClose={closeEmailModal}
                onConfirm={handleConfirmEmail}
                emailRecipient={emailRecipient}
                setEmailRecipient={setEmailRecipient}
                ccRecipients={ccRecipients}
                setCcRecipients={setCcRecipients}
                isSendingEmail={isSendingEmail}
                emailSent={emailSent}
            />
        </div>
    );
}

